<style lang="scss">
    @import '../../sass/variables';

    .data-table-header {
        background-color: $primary;
    }
</style>

<template>
    <div>

        <div class="d-flex data-table-header">
            <div v-if="showItemsPerPageSelect" class="flex-fill justify-content-start input-group">
                <label class="mr-2" for="perPageSelect">Show</label>
                <select v-model.number="perPage" id="perPageSelect" name="perPageSelect" class="mr-2">
                    <option :value="5">5</option>
                    <option :value="10">10</option>
                    <option :value="25">25</option>
                    <option :value="50">50</option>
                    <option :value="200">All</option>
                </select>
                <span>&nbsp;Items</span>
            </div>
            <div v-if="searchEnabled" class="flex-fill justify-content-end input-group">
                <input type="text" v-model="searchString" @input="resetPage" placeholder="Type to Search" />
                <div class="input-group-append">
                    <i class="fa fa-search my-auto"></i>
                </div>
            </div>
        </div>
        
        <div class="data-table-body">
            <table :id="tableId" class="table mb-0" :data-name="tableLabel">
                <thead v-if="!isHeaderRowEmpty">
                    <tr>
                        <th v-for="field in fields"  :key="field.key" @click="onSortBy(field)">
                            {{ field.label }}
                            <template v-if="field.sortable">
                                <span v-show="sortField?.key != field.key"><i class="fa fa-sort" aria-hidden="true"></i></span>
                                <span v-show="sortField && sortField.key == field.key && sortDesc"><i class="fa fa-sort-down" aria-hidden="true"></i></span>
                                <span v-show="sortField && sortField.key == field.key && !sortDesc"><i class="fa fa-sort-up" aria-hidden="true"></i></span>
                            </template>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <!-- empty table alert -->
                    <tr v-if="isTableEmpty || isLoading">
                        <td :colspan="fields.length">
                            <div v-if="isTableEmpty" role="alert" aria-live="polite">
                                <div class="text-center my-2">
                                    {{ emptyTableText }}
                                </div>
                            </div>
                            <div v-if="isLoading" class="spinner-border" role="status">
                                <span class="sr-only">Loading...</span>
                            </div>
                        </td>
                    </tr>
                    
                    <template v-else v-for="(item, index) in tableItems">
                        <tr @click.stop="toggleDetails[item.id] = !toggleDetails[item.id]">
                            <template v-for="field in fields">
                                <td>
                                    <slot :name="field.key" :item="item" :toggleDetails="toggleDetails">
                                        {{ getDisplayValue(item, field) }}
                                    </slot>
                                </td>
                            </template>
                        </tr>
                        <tr v-if="showDetailsRow && toggleDetails[item.id]">
                            <div class="p-3">
                                <slot name="row_details" :item="item"></slot>
                            </div>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <div class="row d-flex flex-row justify-content-center">
            <nav aria-label="Page navigation for list of Connection Invites">
                <ul class="pagination mt-3 mx-3" role="menubar">
                    <li class="page-item" :class="{'disabled': isOnFirstPage}">
                        <button class="page-link" @click.prevent="prevPage" aria-label="Go to previous page">&lt;</button>
                    </li>
                    <!-- show 'first page' button and ellipsis -->
                    <template v-if="startDisplayPage > 1">
                        <li class="page-item">
                            <button class="page-link" @click.prevent="resetPage">1</button>
                        </li>
                        <li class="page-item disabled">
                            <button class="page-link">&hellip;</button>
                        </li>
                    </template>
                    <li v-for="n in pageCountToDisplay" class="page-item" :class="{'active': startDisplayPage + (n-1) == currentPage}" :aria-label="`Go to page ${startDisplayPage + (n-1)}`">
                        <button class="page-link" @click.prevent="setPage(startDisplayPage + (n-1))">{{ startDisplayPage + (n-1) }}</button>
                    </li>
                    <!-- show 'last page' button and ellipsis -->
                    <template v-if="endDisplayPage < pageCount">
                        <li class="page-item disabled">
                            <button class="page-link">&hellip;</button>
                        </li>
                        <li class="page-item">
                            <button class="page-link" @click.prevent="goToLastPage">{{ pageCount }}</button>
                        </li>
                    </template>
                    <li class="page-item" :class="{'disabled': isOnLastPage}">
                        <button class="page-link" @click.prevent="nextPage" aria-label="Go to next page">&gt;</button>
                    </li>
                </ul>
            </nav>
        </div>
    </div>
</template>

<script>
    import _ from 'lodash';
    import moment from 'moment'

    export default {
        props: {
            tableId: {
                type: String,
                required: false,
                default: ""
            },
            tableLabel: {
                type: String,
                required: false,
                default: ""
            },
            fields: {
                type: Array,
                required: true
            },
            items: {
                type: Array,
                required: true,
                default: []
            },
            emptyText: {
                type: String,
                required: false,
                default: "There are no items to display"
            },
            showSearchInput: {
                type: Boolean,
                required: false,
                default: false,
            },
            showItemsPerPageSelect: {
                type: Boolean,
                required: false,
                default: false,
            },
            showDetailsRow: {
                type: Boolean,
                required: false,
                default: false,
            },
        },
        components: {
        },
        data() {
            return {
                sortField:
                {
                    key: null,
                    label: null,
                    sortable: false,
                    compareFn: undefined
                },
                sortDesc: false,
                searchString: "",
                currentPage: 1,
                perPage: 5,
                maxPagesToDisplay: 10,
                sortBy: null,
                loading: false,
                toggleDetails: {}
            };
        },
        methods: {
            toggleSortDir() {
                this.sortDesc = !this.sortDesc
            },
            compareString: function(a, b) {
                if(a < b)
                    return -1;
                if(a > b)
                    return 1;

                return 0;
            },
            compareDate: function(a, b) {
                let aDate = new Date(a);
                let bDate = new Date(b);
                return aDate - bDate;
            },
            /**
             * Return compare function using @param key to get value from objects being compared
             * @param key
             */
             compareByKey: function(aItem, bItem) {
                const key = this.sortField.compareKey ? this.sortField.compareKey : this.sortField.key;
                const aVal = _.get(aItem, key)
                const bVal = _.get(bItem, key)

                // check for falsey values
                if(!aVal)
                    return 1
                if(!bVal)
                    return -1

                return this.compareString(aVal, bVal);
            },
            compareDateByKey: function(aItem, bItem) {
                const key = this.sortField.compareKey ? this.sortField.compareKey : this.sortField.key;
                const aVal = _.get(aItem, key)
                const bVal = _.get(bItem, key)

                return this.compareDate(aVal,bVal);
            },
            onSortBy(field) {
                if(field.sortable) {
                    if(field.key === this.sortField.key) {
                        this.toggleSortDir();
                    } else {
                        this.sortDesc = false;
                    }
                    this.sortField = field;
                    this.resetPage();
                }
            },
            setPage: function(page) {
                this.currentPage = page;
            },
            nextPage: function() {
                if(this.currentPage >= this.pageCount) {
                    this.currentPage = this.pageCount;
                } else {
                    this.currentPage++;
                }
            },
            prevPage: function() {
                if(this.currentPage <= 1) {
                    this.currentPage = 1;
                } else {
                    this.currentPage--;
                }
            },
            // set current page to first page
            resetPage: function() {
                this.setPage(1);
            },
            /**
             * determine function to use for compare based on compareFn in sortField
             */
            resolveCompareFn(fnName) {
                if(fnName && this[fnName]) {
                    return this[fnName]
                }
                return this["compareString"];
            },
            formatDate(dateString, format = 'MM/DD/YYYY') {
                if (dateString && moment(dateString).isValid()) {
                    return moment(dateString).utc().format(format)
                }
                return null
            },
            resolveFormatFn(fnName) {
                let formatFunc = () => { return 0; };

                if(fnName && this[fnName]) {
                    formatFunc = this[fnName];
                }

                return formatFunc;
            },
            fromDisplayKey(item, field) {
                if(field.displayKey) {
                    return _.get(item, field.displayKey);
                }
                return item[field.key];
            },
            formattedItem(item, field) {
                let formatFn;

                if(field.formatFn) {
                    formatFn = this.resolveFormatFn(field.formatFn);
                    return formatFn(this.fromDisplayKey(item, field), field.formatOptions.format);
                }
                return this.fromDisplayKey(item, field);
            },
            getDisplayValue(item, field) {
                let displayVal; 
                
                if(field.displayKey) {
                    displayVal = item[field.displayKey]
                } else {
                    displayVal = item[field.key];
                }

                if(field.formatFn) {
                    let formatFn = this.resolveFormatFn(field.formatFn);
                    displayVal = formatFn(this.fromDisplayKey(item, field), field.formatOptions.format);
                }

                return displayVal;
            },
            goToLastPage: function() {
                this.setPage(this.pageCount);
            }
        },
        computed: {
            tableItems: function() {
                return this.paginatedItems;
            },
            isLoading: function() {
                return this.loading;
            },
            sortedItems: function() {
                let sortedItems = this.filteredBySearchString;

                if(this.sortField) {
                    let compFunc = this.resolveCompareFn(this.sortField.compareFn);
                    sortedItems = sortedItems.toSorted(compFunc);
                    sortedItems = this.sortDesc ? sortedItems.toReversed() : sortedItems ;
                }

                return sortedItems;
            },
            filteredBySearchString: function() {
                if(this.searchString) {
                    return this.items.filter((item) => {
                        let searchableItem = {
                            ...item
                        };
                        return JSON.stringify(searchableItem).search(new RegExp(this.searchString, "gi")) >= 0;
                    });
                } else {
                    return this.items;
                }
            },
            sortFields: function () {
                if (this.fields) {
                    return this.fields
                        .filter(f => f.sortable)
                        .map(f => { return { text: f.label, value: f.key } })
                }
                return new Array();
            },
            pageCount: function() {
                return Math.ceil(this.filteredBySearchString.length/this.perPage);
            },
            pageCountToDisplay: function() {
                return this.pageCount > this.maxPagesToDisplay ? this.maxPagesToDisplay : this.pageCount;
            },
            paginatedItems: function() {
                let start = (this.currentPage-1) * this.perPage;
                let paginatedItems = this.sortedItems;
                return paginatedItems.slice(start, start + this.perPage);
            },
            isOnFirstPage: function() {
                return this.currentPage == 1;
            },
            isOnLastPage: function() {
                return this.currentPage == this.pageCount;
            },
            /**
             * Compute first page to show in pagination window
             */
            startDisplayPage: function() {
                // set start display page so that current page is approx middle of page list
                let startPage = this.currentPage - Math.ceil(this.maxPagesToDisplay/2);
                let endPage = startPage + (this.maxPagesToDisplay - 1);

                if(endPage > this.pageCount) {
                    startPage -= (endPage - this.pageCount);
                }

                if(startPage < 1) {
                    startPage = 1;
                }

                return startPage;
            },
            /**
             * Compute last page to show in pagination window
             */
            endDisplayPage: function() {
                return this.startDisplayPage + this.maxPagesToDisplay - 1;
            },
            searchEnabled: function() {
                return this.showSearchInput
            },
            isTableEmpty: function() {
                return this.tableItems.length == 0;
            },
            emptyTableText() {
                if(
                    this.searchEnabled && 
                    this.isTableEmpty && 
                    this.searchString.length > 0
                ){
                    return "No results for this search text";
                }
                return this.emptyText;
            },
            isHeaderRowEmpty: function() {
                return this.fields.reduce((accumulator, value) => {
                    return accumulator && !(value.label);
                }, true);
            },
        },
        watch: {
            /**
             * Set remainder of sortField keys when sort dropdown is selected
             */
            "sortField.key"(newValue) {
                let newSortField = this.fields.find((field) => {
                    return field.key === newValue;
                });
                Object.assign(this.sortField,  { ...newSortField });
            },
        },
        mounted() {
        }
    }
</script>

